import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Building2, Check, ChevronDown, MapPin, Search, Settings2 } from "lucide-react";
import config from "../../config";
import { useFacility } from "../../context/FacilityContext";

/**
 * Location switcher for the top bar (design: components/FacilitySwitcher.tsx).
 * Picks one facility or the whole organisation, using the app's real
 * FacilityContext (/api/facilities/context).
 *
 * Doctors per location come from /api/staff?role=doctor&facility_id=…, loaded
 * when the panel opens. The design also shows chairs; the backend stores no
 * chairs, so they are left out (see the integration report).
 */
const ALL = "all";
export const shortName = (name = "") => name.split(",").pop()?.trim() || name;

const detail = (...parts) => parts.filter((p) => p !== null && p !== undefined && p !== "").join(" · ");

export function FacilitySwitcher({ org }) {
  const fac = useFacility();
  const facilities = (fac.facilities || []).filter((f) => f.is_active !== 0 && f.is_active !== false);
  const scope = fac.selectedFacilityId === ALL ? ALL : fac.selectedFacilityId;
  const current = scope === ALL || scope == null ? null : facilities.find((f) => f.id === scope) ?? null;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrap = useRef(null);
  const panel = useRef(null);
  const [pos, setPos] = useState({ top: 72, right: 16 });
  const searchable = facilities.length > 5;
  const [doctors, setDoctors] = useState({}); // facility id → active doctors

  useEffect(() => {
    if (!open || !facilities.length) return undefined;
    let alive = true;
    const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    Promise.all(facilities.map((f) => axios.get(`${config.API_BASE_URL}/api/staff`, { headers, params: { role: "doctor", facility_id: f.id, is_active: true } })
      .then((r) => [f.id, r.data?.success ? (r.data.data || []).length : null]).catch(() => [f.id, null])))
      .then((pairs) => { if (alive) setDoctors(Object.fromEntries(pairs)); });
    return () => { alive = false; };
  }, [open, facilities.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const place = () => {
    const b = wrap.current?.getBoundingClientRect();
    if (b) setPos({ top: Math.floor(b.bottom + 8), right: Math.floor(Math.max(16, window.innerWidth - b.right)) });
  };
  useEffect(() => {
    if (!open) return undefined;
    place();
    const onDown = (e) => { const t = e.target; if (!wrap.current?.contains(t) && !panel.current?.contains(t)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { document.removeEventListener("mousedown", onDown); window.removeEventListener("keydown", onKey); window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open]);
  useEffect(() => { if (!open) setQ(""); }, [open]);

  const pick = (id) => { if (id === ALL) fac.selectAll(); else fac.selectFacility(id); setOpen(false); };
  const list = facilities.filter((f) => !q || `${f.name} ${f.city || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div ref={wrap} className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Location: ${current ? current.name : `All locations, ${org}`}. Change location`}
        className={`flex h-11 min-w-0 max-w-[min(360px,calc(100vw-120px))] items-center gap-2.5 rounded-full bg-paper pl-2 pr-4 text-left shadow-soft ring-1 hover:bg-mist ${open ? "ring-ink/30" : "ring-line"}`}
      >
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${current ? "bg-sky-soft text-ink" : "bg-ink text-paper"}`}>
          {current ? <MapPin size={16} strokeWidth={1.75} /> : <Building2 size={16} strokeWidth={1.75} />}
        </span>
        <span className="flex min-w-0 flex-col leading-tight">
          <span className="truncate text-caption text-muted">{org}</span>
          <span className="truncate text-small font-semibold">{current ? shortName(current.name) : "All locations"}</span>
        </span>
        <ChevronDown size={16} strokeWidth={1.75} className={`shrink-0 text-muted transition-transform duration-200 ease-out ${open ? "rotate-180" : ""}`} />
      </button>

      {open && createPortal(
        <div ref={panel} style={{ top: pos.top, right: pos.right }} className="olli animate-fade-in fixed z-[80] w-[340px] max-w-[calc(100vw-32px)] overflow-hidden rounded-lg bg-paper text-ink shadow-lift ring-1 ring-line">
          <div className="border-b border-line px-4 py-3">
            <p className="text-small font-semibold">Switch location</p>
            <p className="text-caption text-muted">Pages show data for the location you pick</p>
          </div>
          {searchable && (
            <label className="mx-3 mt-3 flex h-10 items-center gap-2 rounded-full bg-paper px-3 ring-1 ring-line focus-within:ring-ink">
              <Search size={16} strokeWidth={1.75} className="text-muted" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search locations" className="min-w-0 flex-1 bg-transparent text-small outline-none placeholder:text-muted" />
            </label>
          )}
          <ul role="listbox" aria-label="Locations" className="max-h-[360px] overflow-y-auto p-2">
            {/* "All locations" is an owner/admin view; staff only see the facilities they work at. */}
            {!q && fac.canSelectAll && (
              <li>
                <button type="button" role="option" aria-selected={scope === ALL} onClick={() => pick(ALL)} className="flex w-full items-center gap-3 rounded-md px-2.5 py-2.5 text-left hover:bg-mist">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink text-paper"><Building2 size={18} strokeWidth={1.75} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-small font-semibold">All locations</span>
                    <span className="block truncate text-caption text-muted tabular-nums">{detail(org, `${facilities.length} locations`)}</span>
                  </span>
                  {scope === ALL && <Check size={18} strokeWidth={2} className="shrink-0" aria-hidden />}
                </button>
              </li>
            )}
            {!q && fac.canSelectAll && <li aria-hidden className="mx-2.5 my-1 border-t border-line" />}
            {list.map((f) => (
              <li key={f.id}>
                <button type="button" role="option" aria-selected={scope === f.id} onClick={() => pick(f.id)} className="flex w-full items-center gap-3 rounded-md px-2.5 py-2.5 text-left hover:bg-mist">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-soft text-ink"><MapPin size={18} strokeWidth={1.75} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2"><span className="truncate text-small font-semibold">{shortName(f.name)}</span>{!!f.is_primary && <span className="shrink-0 rounded-xs bg-mist px-1.5 py-0.5 text-caption font-medium text-muted">Main</span>}</span>
                    <span className="block truncate text-caption text-muted tabular-nums">{detail((f.city || "").split(",")[0], doctors[f.id] != null ? `${doctors[f.id]} doctors` : null)}</span>
                  </span>
                  {scope === f.id && <Check size={18} strokeWidth={2} className="shrink-0" aria-hidden />}
                </button>
              </li>
            ))}
            {q && list.length === 0 && <li className="px-3 py-6 text-center text-small text-muted">No location matches "{q}"</li>}
          </ul>
          <Link to="/settings/facilities" onClick={() => setOpen(false)} className="flex items-center gap-2 border-t border-line px-4 py-3 text-small font-semibold text-sky-deep hover:bg-mist">
            <Settings2 size={16} strokeWidth={1.75} />Manage locations
          </Link>
        </div>,
        document.body,
      )}
    </div>
  );
}
